import { useResponse } from 'contexts/response';
import { Fragment, useCallback } from 'react';

import { UnitOrder } from 'types';

const isUnitOrder = (json: any): json is UnitOrder =>
  typeof json === 'object' && 'allowed' in json && 'customer' in json && 'unitNumber' in json;

const isUnitOrderList = (json: any): json is UnitOrder[] =>
  typeof json === 'object' && 0 in json && isUnitOrder(json[0]);

export const useOrderApi = () => {
  const { handleError, setSuccess } = useResponse();

  const postOrders = useCallback(
    async (unitOrders: UnitOrder[]): Promise<UnitOrder[] | null> => {
      const url: URL = new URL('/api/unit-orders', window.location.origin);
      const init: RequestInit = {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }),
        body: JSON.stringify(unitOrders)
      };

      try {
        const response = await fetch(url, init);
        const json = await response.json();

        await Promise.allSettled([response, json]);

        if (response.ok && isUnitOrderList(json)) {
          setSuccess(
            <div className="grid-3">
              {json.map(({ allowed, customer, unitNumber }) => (
                <Fragment key={unitNumber ?? '' + customer?.id ?? ''}>
                  <span>{customer?.name ?? 'ACCOUNT'}</span>
                  <span className="justify-self-center">{!allowed ? 'STOP' : 'ALLOW'}</span>
                  <span className="justify-self-end">{unitNumber ?? 'UNIT'}</span>
                </Fragment>
              ))}
            </div>
          );
          return json;
        }

        switch (response.status) {
          case 500:
            handleError('INTERNAL SERVER ERROR');
            return null;
          case 403:
            handleError('FORBIDDEN');
            return null;
          case 401:
            handleError('UNAUTHORIZED');
            return null;
          default:
            handleError(json.message?.match(/"[^"]+"/)?.[0] ?? response.statusText);
            return null;
        }
      } catch (e) {
        handleError(e);
        return null;
      }
    },
    [handleError, setSuccess]
  );

  const editOrder = useCallback(
    async (unitOrder: UnitOrder): Promise<UnitOrder | null> => {
      const url: URL = new URL(`/api/unit-orders/${unitOrder.id}`, window.location.origin);
      const init: RequestInit = {
        method: 'PUT',
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }),
        body: JSON.stringify(unitOrder)
      };

      try {
        const response = await fetch(url, init);
        const json = await response.json();

        await Promise.allSettled([response, json]);

        if (response.ok && isUnitOrder(json)) {
          setSuccess(
            <div className="grid-3">
              <span>{json.customer?.name ?? 'ACCOUNT'}</span>
              <span className="justify-self-center">{!json.allowed ? 'STOP' : 'ALLOW'}</span>
              <span className="justify-self-end">{json.unitNumber ?? 'UNIT NUMBER'}</span>
            </div>
          );
          return json;
        }

        switch (response.status) {
          case 500:
            handleError('INTERNAL SERVER ERROR');
            return null;
          case 403:
            handleError('FORBIDDEN');
            return null;
          case 401:
            handleError('UNAUTHORIZED');
            return null;
          default:
            handleError(json.message?.match(/"[^"]+"/)?.[0] ?? response.statusText);
            return null;
        }
      } catch (e) {
        handleError(e);
        return null;
      }
    },
    [handleError, setSuccess]
  );

  const deleteOrder = useCallback(
    async (unitOrder: UnitOrder): Promise<true | null> => {
      const url: URL = new URL(`/api/unit-orders/${unitOrder.id}`, window.location.origin);
      const init: RequestInit = {
        method: 'DELETE',
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        })
      };

      try {
        const response = await fetch(url, init);

        if (response.ok) {
          setSuccess(`Unit order #${unitOrder.id} deleted.`);
          return true;
        }

        const json = await response.json();

        switch (response.status) {
          case 500:
            handleError('INTERNAL SERVER ERROR');
            return null;
          case 403:
            handleError('FORBIDDEN');
            return null;
          case 401:
            handleError('UNAUTHORIZED');
            return null;
          default:
            handleError(json.message?.match(/"[^"]+"/)?.[0] ?? response.statusText);
            return null;
        }
      } catch (e) {
        handleError(e);
        return null;
      }
    },
    [handleError, setSuccess]
  );

  return { deleteOrder, editOrder, postOrders };
};
